export const API_ORIGIN =
  import.meta.env.PROD ? '' : 'http://localhost:5162';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? `${API_ORIGIN}/api`;


export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let _accessToken: string | null = null;

// Retourne l'access token conservé uniquement en mémoire.
export function getAccessToken(): string | null {
  return _accessToken;
}


// Met à jour l'access token uniquement en mémoire.
// Après un rechargement de page, il devra être récupéré grâce au refresh token HttpOnly.
export function setAccessToken(token: string | null) {
  _accessToken = token;
}


export function clearAuth() {
  setAccessToken(null);
}

// Refresh token attempt with singleton promise to prevent multiple parallel refreshes
let refreshPromise: Promise<boolean> | null = null;

// Renouvelle la session grâce au refresh token envoyé automatiquement par le cookie HttpOnly.
async function _doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      clearAuth();
      return false;
    }

    const data = await res.json();
    if (data.accessToken) setAccessToken(data.accessToken);

    return true;
  } catch {
    clearAuth();
    return false;
  }
}

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = _doRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function api<T>(path: string, options: any & { skipAuthRefresh?: boolean } = {}): Promise<T> {
  const { skipAuthRefresh, ...fetchOptions } = options;

  const url = `${API_BASE_URL}${path}`;
  const token = getAccessToken();

  const headers = new Headers(fetchOptions.headers);

  if (
    !headers.has('Content-Type') &&
    !(fetchOptions.body instanceof FormData) &&
    !(fetchOptions.body instanceof URLSearchParams)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (response.status === 401 && !skipAuthRefresh) {
    const refreshed = await tryRefresh();

    if (refreshed) {
      const newToken = getAccessToken();
      const newHeaders = new Headers(fetchOptions.headers);

      if (
        !newHeaders.has('Content-Type') &&
        !(fetchOptions.body instanceof FormData) &&
        !(fetchOptions.body instanceof URLSearchParams)
      ) {
        newHeaders.set('Content-Type', 'application/json');
      }

      if (newToken) {
        newHeaders.set('Authorization', `Bearer ${newToken}`);
      }

      response = await fetch(url, {
        ...fetchOptions,
        headers: newHeaders,
      });
    } else {
      throw new ApiError(401, 'Session expirée');
    }
  }

  return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Erreur ${response.status}`;

    try {
      const errorData = await response.json();
      message =
        errorData.message ||
        errorData.title ||
        errorData.detail ||
        message;
    } catch {
      // ignore
    }

    throw new ApiError(response.status, message);
  }

  const text = await response.text();

  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}